import type { MouseEventHandler } from 'react'
import React, { useCallback } from 'react'
import styled from 'styled-components'
import type { Coupon, NonExternalCoupon } from 'shared-types/Coupon'
import { isLinked, isExternal } from 'mobx/Coupons/utils'
import TypographyPro from 'themes/TypographyPro'
import MuiArrow from '@material-ui/icons/ArrowForwardIosOutlined'
import type { ButtonBaseProps } from 'components/common/ButtonBase'
import ButtonBase from 'components/common/ButtonBase'
import { getTranslatedTextByKey } from 'utils/utils'
import { mediaQueriesBreakpoints } from 'styles/mixins'
import ImageNext from 'components/common/ImageNext'
import { inject, observer } from 'mobx-react'
import type _Infra from 'mobx/Infra'
import { getDefaultImgUrl } from 'mobx/CouponFlow/utils'
import CouponButton, { ButtonType, Origin } from './CouponButton'
import CouponsStore from 'mobx/Coupons/store'
import type { LanguageLocale } from 'utils/language'
import { getRulesOfCoupon, toButtonType } from './modal/utils'
import type _Account from 'mobx/Account'
import type CouponError from 'mobx/Coupons/errors'
import { SnackbarStatus } from 'mobx/Infra/Infra.type'

interface Props {
	coupon: Coupon
	locale: LanguageLocale
	onViewInfo?: MouseEventHandler<HTMLDivElement | HTMLButtonElement>
	onApply?: (coupon: NonExternalCoupon) => void
	setDisableInteractions?: React.Dispatch<React.SetStateAction<boolean>>
	disabled?: boolean
}

const Card = styled.div`
	display: flex;
	flex-direction: column;

	cursor: pointer;
	width: 100%;

	@media ${mediaQueriesBreakpoints.md} {
		width: 16.0625rem;
	}

	gap: 0.75rem;
	padding-bottom: 0.75rem;

	background: var(--clear);
	box-shadow: var(--defaultShadow1);
	border-radius: 4px;
`

const ImageContainer = styled.div`
	position: relative;
	width: 100%;
	aspect-ratio: 191 / 100;
`

const Line = styled.div`
	width: 100%;
	height: 1px;

	background-image: linear-gradient(to right, var(--disable) 50%, rgba(255, 255, 255, 0) 0%);
	background-position: top;
	background-size: 0.75em 1px;
	background-repeat: repeat-x;
`

const Arrow = styled(MuiArrow)`
	display: flex;
	width: 0.5em;
	height: 0.5em;
	justify-content: center;
	align-items: center;

	cursor: pointer;
	color: var(--strokeMenuTitle);
`

const TextContainer = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 0.25rem;
`

const Text = styled(TypographyPro).attrs({ variant: 'BodySmall' })`
	color: var(--strokeMenuTitle);
	text-overflow: ellipsis;
	overflow: hidden;

	display: -webkit-box;
	-webkit-box-orient: vertical;
	-webkit-line-clamp: 2;
`
const Title = styled(TypographyPro).attrs({ variant: 'BodyRegularHighlighted' })`
	color: var(--strokeMenuTitle);
	text-overflow: ellipsis;
	overflow: hidden;

	display: -webkit-box;
	-webkit-box-orient: vertical;
	-webkit-line-clamp: 2;
	align-self: stretch;
	margin-bottom: auto;
`

const BoldText = styled(TypographyPro).attrs({ variant: 'BodySmallHighlighted' })`
	color: var(--strokeMenuTitle);
`

const ActionText = styled(TypographyPro).attrs({ variant: 'BodySmallClickable' })`
	color: var(--strokeMenuTitle);
	cursor: pointer;
`

const Button = styled(ButtonBase).attrs({ fullWidth: false })`
	display: flex;

	padding: 0.5em 1em;
`

const UnapplyButton = (props: Omit<ButtonBaseProps, 'fullWidth'>) => <Button $type="secondary" {...props} />

const InfoContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;

	flex-grow: 1;
	padding-top: 0.25rem;
	padding: 0 0.75rem;
`

const ActionsContainer = styled.div`
	display: flex;
	justify-content: space-between;
	width: 100%;
	gap: 1em;
`

const ViewInfoAndExpiryContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
`

const CounterContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	position: relative;
	float: right;
	padding: 0 0.25rem;
	border: 1px solid var(--primary);
	border-radius: 0.25rem;

	min-width: 33px;
	height: 33px;
	top: 10px;
	right: 10px;
	background: var(--clear);

	z-index: 2;
`

const CounterText = styled(TypographyPro).attrs({ variant: 'BodyRegular' })`
	color: var(--strokeMenuTitle);
`

const CouponCard: React.FC<Props & { Infra: typeof _Infra; Account: typeof _Account }> = ({
	coupon: unlocalizedCoupon,
	locale,
	disabled = false,
	setDisableInteractions,
	onViewInfo,
	onApply,
	Infra,
	Account,
}) => {
	const coupon = CouponsStore.localizeCoupon(unlocalizedCoupon, locale)
	const external = isExternal(coupon)
	const { applied, save, signIn, actionType } = getRulesOfCoupon(unlocalizedCoupon, Account.isAuthenticated())

	const handleError = useCallback(
		(error: CouponError, actions: Record<ButtonType, () => Promise<void>>, disableSnackbar?: boolean) => {
			if (error.continueFlow && !disableSnackbar) {
				Infra?.showSnackbar({
					status: SnackbarStatus.INFO,
					message: error.text(),
					key: 'coupons-error-snackbar',
					snackId: 'coupons-error-snackbar',
					isAttachedToElement: false,
				})

				actions[ButtonType.APPLY]()
			} else {
				setDisableInteractions?.(false)
			}
		},
		[Infra, setDisableInteractions]
	)

	const content = () => (
		<Card data-testid="coupon-card-container" onClick={disabled ? () => {} : onViewInfo}>
			<ImageContainer data-testid="coupon-card-image-container">
				{isLinked(coupon) && coupon.usesLeft && coupon.usesLeft > 1 && (
					<CounterContainer data-testid="coupon-counter-container">
						<CounterText>
							{getTranslatedTextByKey('eCommerce.coupons.card.count', 'x')}
							{coupon.usesLeft}
						</CounterText>
					</CounterContainer>
				)}
				<ImageNext src={coupon.img} fill alt={coupon.description ?? coupon.title} fallbackSrc={getDefaultImgUrl(Infra as typeof _Infra)} />
			</ImageContainer>
			<InfoContainer data-testid="coupon-card-info-container">
				<Title data-testid="coupon-card-title">{coupon.title}</Title>
				<Line />
				<ActionsContainer>
					<ViewInfoAndExpiryContainer>
						{!external && (
							<TextContainer
								style={{ flexWrap: 'nowrap' }}
								onClick={(e) => {
									e.stopPropagation()
									if (disabled) {
										return
									}
									if (onViewInfo) {
										onViewInfo(e)
									}
								}}
							>
								<ActionText>{getTranslatedTextByKey('eCommerce.coupons.card.viewInfo', 'View Info')}</ActionText>
								<Arrow />
							</TextContainer>
						)}
						{coupon.formattedExpiration && (
							<TextContainer>
								<Text>{getTranslatedTextByKey('eCommerce.coupons.card.expiry', 'Exp:')}</Text>
								<BoldText>{coupon.formattedExpiration}</BoldText>
							</TextContainer>
						)}
					</ViewInfoAndExpiryContainer>
					<CouponButton
						disabled={disabled && !applied}
						variant={toButtonType(actionType, { save, applied, signIn })}
						textOverride={toButtonType(actionType, { save: false, signIn: false, applied })}
						origin={Origin.CARD}
						coupon={unlocalizedCoupon}
						Element={applied ? UnapplyButton : Button}
						onApply={onApply}
						onError={handleError}
					/>
				</ActionsContainer>
			</InfoContainer>
		</Card>
	)

	return content()
}

export default inject('Infra', 'Account')(observer(CouponCard)) as React.FC<Props>
